import React from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight, Shield, CheckCircle, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '@/contexts/TenantContext';
import { isRootDomain, getPublicUrl, getBaseUrl } from '@/lib/utils';

interface TenantBannerProps {
  className?: string;
}

export const TenantBanner: React.FC<TenantBannerProps> = ({ className = '' }) => {
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const pathHasTenant = typeof window !== 'undefined' && /(?:^|\/)(?:business)?\/site\/[^/]+/.test(window.location.pathname);
  const isMainSite = isRootDomain() || !pathHasTenant;

  // Default banner configuration
  // Main site: original deep blue; Tenants: light beige (Virtuzone style) by default.
  const defaultConfig = {
    title: 'Comprehensive Business Insurance',
    subtitle: 'Protect your business with our tailored insurance coverage designed for UAE enterprises, provided by our broker partner YallaCompare',
    backgroundColor: isMainSite ? '#1E40AF' : '#f2f3ee',
    textColor: isMainSite ? '#FFFFFF' : '#FFFFFF',
    ctaText: 'Get Your Quote',
    ctaLink: '/decision-tree',
  };

  // Merge with defaults but ignore null/undefined/empty strings so defaults persist
  const rawConfig = (tenant?.bannerConfig || {}) as Partial<typeof defaultConfig> & Record<string, any>;
  const cleanedConfig = Object.fromEntries(
    Object.entries(rawConfig).filter(([, v]) => v !== null && v !== undefined && v !== '')
  ) as Partial<typeof defaultConfig>;
  const bannerConfig = { ...defaultConfig, ...cleanedConfig } as typeof defaultConfig;

	  // Determine active tenant for special-case styling
	  const pathMatch = window.location.pathname.match(/^(?:\/business)?\/site\/([^/]+)/);
	  const activeSub = tenant?.subdomain || (pathMatch ? pathMatch[1] : undefined);
	  const isDCC = activeSub === 'dubaicommercity';
	  const isIncorpify = activeSub === 'incorpify' || activeSub === 'incorpify-ai';
	  const INCORPIFY_BLUE = '#2563EB';
	  const isVirtuzone = activeSub === 'virtuzone';


	  // DCC background: attempt known file names in /public/Logos/dcc first, then fall back
	  const [dccBg, setDccBg] = React.useState<string | undefined>(undefined);
	  React.useEffect(() => {
	    if (!isDCC) return;
	    const candidates = [
	      getPublicUrl('/Logos/dcc/dubai-commercity.jpg'),
	      getPublicUrl('/Logos/dcc/dubai-commercity.jpeg'),
	      getPublicUrl('/Logos/dcc/dubai-commercity.png'),
	      getPublicUrl('/backgrounds/dcc-background-2.jpeg'),
	    ];
	    let cancelled = false;
	    let i = 0;
	    const tryNext = () => {
	      if (cancelled || i >= candidates.length) return;
	      const src = candidates[i++];
	      const img = new Image();
	      img.onload = () => { if (!cancelled) setDccBg(src); };
	      img.onerror = () => { if (!cancelled) tryNext(); };
	      img.src = src;
	    };
	    tryNext();
	    return () => { cancelled = true; };
	  }, [isDCC]);


	  // Resolve background image URL safely for both root and /business deployments
	  const rawBg = (bannerConfig as any).backgroundImage as string | undefined;
	  const resolvedBg = (() => {
	    // DCC: prefer discovered bg via candidates; fallback to known local background
	    if (isDCC) return dccBg || getPublicUrl('/backgrounds/dcc-background-2.jpeg');
	    if (!rawBg) return undefined;
	    if (/^https?:\/\//.test(rawBg)) return rawBg;
	    const base = getBaseUrl();
	    const p = rawBg.startsWith('/') ? rawBg : `/${rawBg}`;
	    if (base && base !== '/' && p.startsWith(`${base}/`)) return p; // already base-prefixed
	    return getPublicUrl(p);
	  })();


	  // Dev-only: log what URL we ended up using so we can see if the image should be fetched
	  if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname.endsWith('.localhost'))) {
	    // eslint-disable-next-line no-console
	    console.log('[TenantBanner] BG', { rawBg, base: getBaseUrl(), resolvedBg, MARKER: 'dcc-hard-local-public' });
	    if (resolvedBg) {
	      try {
	        const testImg = new Image();
	        testImg.onload = () => console.info('[TenantBanner] test image loaded', { src: resolvedBg, width: testImg.width, height: testImg.height });
	        testImg.onerror = () => console.error('[TenantBanner] test image failed to load', { src: resolvedBg });
	        testImg.src = resolvedBg;
	      } catch {}
	    }
	  }


	  // Use Start Matrix button color as base for card backgrounds; fallback to brand red
	  const startMatrixBase = tenant?.colorScheme?.startMatrixButtonColor || '#da1884';
	  function toRgba(input: string, alpha: number) {
	    const rgb = parseColorToRgb(input);
	    if (rgb) return `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
	    return `rgba(218,24,132,${alpha})`; // #da1884 fallback
	  }
	  const cardBgColor = isDCC ? '#4B1F5E' : toRgba(startMatrixBase, 0.15);
	  const chipBgColor = isDCC ? 'rgba(255,255,255,0.2)' : toRgba(startMatrixBase, 0.20);


	  // Compute effective text color for contrast; prefer white on dark backgrounds
	  const bgColor = isIncorpify
	    ? '#0a0a0a'
	    : (bannerConfig.backgroundColor || defaultConfig.backgroundColor);

  // Robust color parsing: supports #rgb, #rrggbb, rgb(), rgba(); returns null on failure
  function parseColorToRgb(input: string): { r: number; g: number; b: number } | null {
    if (!input) return null;
    const s = input.trim();
    // Hex formats
    if (s.startsWith('#')) {
      let clean = s.replace('#', '');
      if (clean.length === 3) {
        clean = clean.split('').map(c => c + c).join('');
      }
      if (clean.length === 6) {
        const bigint = parseInt(clean, 16);
        return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
      }
      return null;
    }
    // rgb/rgba formats
    const m = s.match(/^rgba?\(\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})(?:\s*,\s*(?:0|1|0?\.[0-9]+))?\s*\)$/i);
    if (m) {
      const r = Math.max(0, Math.min(255, parseInt(m[1], 10)));
      const g = Math.max(0, Math.min(255, parseInt(m[2], 10)));
      const b = Math.max(0, Math.min(255, parseInt(m[3], 10)));
      return { r, g, b };
    }
    return null;
  }

  function luminance(r: number, g: number, b: number) {
    const a = [r, g, b].map(v => {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
  }

  function contrast(c1: string, c2: string) {
    const rgb1 = parseColorToRgb(c1);
    const rgb2 = parseColorToRgb(c2);
    if (!rgb1 || !rgb2) throw new Error('unparsable color');
    const L1 = luminance(rgb1.r, rgb1.g, rgb1.b) + 0.05;
    const L2 = luminance(rgb2.r, rgb2.g, rgb2.b) + 0.05;
    return L1 > L2 ? L1 / L2 : L2 / L1;
  }

  // Choose an accessible text color. Prefer configured value, but if contrast is poor,
  // automatically switch to white on dark backgrounds or black on light backgrounds.
  const preferredText = bannerConfig.textColor || defaultConfig.textColor;
  let textColor = preferredText;
  try {
    const contrastRatio = contrast(bgColor, preferredText);
    if (contrastRatio < 4.5) {
      const bgRgb = parseColorToRgb(bgColor);
      if (!bgRgb) throw new Error('bg unparsable');
      const bgLum = luminance(bgRgb.r, bgRgb.g, bgRgb.b);
      textColor = bgLum < 0.5 ? '#FFFFFF' : '#111827'; // white on dark bg, near-black on light bg
    }
  } catch {
    // If parsing fails, choose safe default (white) for readability
    textColor = '#FFFFFF';
  }
  // DCC override: force all banner text to black for readability
  if (isDCC) {
    textColor = '#111827';
  }


  const handleCtaClick = () => {
    if (!bannerConfig.ctaLink) return;
    // Ensure tenant-aware navigation: prefix with /site/:subdomain when present
    const subMatch = window.location.pathname.match(/^(?:\/business)?\/site\/([^/]+)/);
    const subPrefix = subMatch ? `/site/${subMatch[1]}` : '';
    navigate(`${subPrefix}${bannerConfig.ctaLink}`);
  };

  const scrollToProducts = () => {
    const productsSection = document.getElementById('insurance-products');
    if (productsSection) {
      productsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const features = [
    'UAE Law Compliant Coverage',
    'Expert Insurance Consultation',
    'Competitive Rates',
    'Fast Quote Processing'
  ];

  return (
    <section
      className={`relative py-20 px-4 overflow-hidden text-white ${className}`}
      style={{
        backgroundColor: bgColor,
        color: textColor,
        backgroundImage: resolvedBg ? `url(${resolvedBg})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Background overlay for better text readability when using background image */}
      {resolvedBg && (
        <div
          className="absolute inset-0"
          style={{
            backgroundColor: isDCC ? '#ffffff' : bannerConfig.backgroundColor,
            opacity: isDCC ? 0.35 : 0.8
          }}
        />
      )}

      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute -top-40 -right-40 w-80 h-80 rounded-full opacity-10"
          style={{ backgroundColor: isIncorpify ? '#232323' : '#ffffff' }}
        />
        <div
          className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full opacity-10"
          style={{ backgroundColor: isIncorpify ? '#232323' : '#ffffff' }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8" style={{ color: textColor }}>
            <div className="space-y-6">
              <div className="space-y-3">
                <h1 className={`text-4xl md:text-5xl lg:text-6xl font-bold leading-tight ${isDCC ? 'bg-white/15 backdrop-blur-sm rounded-md px-4 py-2 border border-white/30 inline-block' : ''}`} style={{ color: textColor }}>
                  {bannerConfig.title}
                </h1>

                {/* Prominent YallaCompare Branding */}
                <div className={`flex items-center gap-2 opacity-90 ${isDCC ? 'bg-white/15 backdrop-blur-sm rounded-full px-4 py-2 border border-white/30 inline-flex' : ''}`}>
                  <div className="h-px bg-current flex-1 max-w-12"></div>
                  <p className="text-sm md:text-base font-medium tracking-wide">
                    Powered by YallaCompare Insurance Broker LLC #20
                  </p>
                  <div className="h-px bg-current flex-1 max-w-12"></div>
                </div>
              </div>

              <p className={`text-xl md:text-2xl opacity-90 leading-relaxed ${isDCC ? 'bg-white/15 backdrop-blur-sm rounded-md px-4 py-2 border border-white/30 inline-block' : ''}`}>
                {bannerConfig.subtitle}
              </p>
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <div key={index} className={`flex items-center gap-3 ${isDCC ? 'bg-white/15 backdrop-blur-sm rounded-md px-3 py-2 border border-white/30' : ''}`}>
                  <CheckCircle className="h-5 w-5 flex-shrink-0 opacity-80" style={{ color: isDCC ? '#FFFFFF' : undefined }} />
                  <span className="text-sm md:text-base opacity-90">{feature}</span>
                </div>
              ))}
            </div>

	            {/* Helper pill above CTAs */}
	            <div className="mt-2 inline-flex items-center gap-2 max-w-fit bg-white/15 backdrop-blur-sm rounded-full px-4 py-2 border border-white/30 shadow-sm">
	              <Sparkles className="h-4 w-4 opacity-90" />
	              <span className="text-sm md:text-base opacity-95">Don’t know what your business needs? Use our smart decision engine by clicking “Start Matrix” below.</span>
	            </div>
	
	            {/* CTA Buttons */}
	            <div className="flex flex-col sm:flex-row gap-4">
	              <Button
	                size="lg"
	                onClick={scrollToProducts}
	                className="shadow-lg font-semibold text-lg px-10 py-4 h-auto bg-white text-black hover:bg-gray-100"
	              >
	                View Products
	                <ArrowRight className="ml-2 h-5 w-5" />
	              </Button>
	
	              <Button
	                size="lg"
	                onClick={handleCtaClick}
	                className={`font-semibold text-lg px-10 py-4 h-auto shadow-xl hover:shadow-2xl transition-all duration-300 group`}
	                style={{
	                  backgroundColor: isIncorpify
	                    ? INCORPIFY_BLUE
	                    : (tenant?.colorScheme?.startMatrixButtonColor || (isDCC ? '#4B1F5E' : undefined)),
	                  color: (isIncorpify || tenant?.colorScheme?.startMatrixButtonColor || isDCC)
	                    ? '#FFFFFF'
	                    : undefined,
	                }}
	              >
	                Start Matrix
	                <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
	              </Button>
	            </div>

            {/* Trust indicators */}
            <div className="flex items-center gap-6 pt-4 opacity-80">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                <span className="text-sm">UAE Licensed</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                <span className="text-sm">Instant Quotes</span>
              </div>
            </div>

            {/* Required Legal Text - Enhanced Visibility */}
            <div className="pt-6 border-t border-white/20">
              <p className="text-sm opacity-85 leading-relaxed font-medium">
                Powered by YallaCompare Insurance Broker LLC #20
              </p>
            </div>
          </div>

          {/* Visual Element */}
          <div className="hidden lg:block">
            <div className="relative max-w-md ml-auto">
              {/* Insurance cards mockup */}
              <div className="space-y-4">
                <div
                  className="rounded-xl p-6 border"
                  style={{ backgroundColor: cardBgColor, borderColor: isDCC ? '#4B1F5E' : `${bannerConfig.textColor}20`, color: isDCC ? '#FFFFFF' : undefined }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: chipBgColor }}
                    >
                      <Shield className="h-5 w-5" style={{ color: isDCC ? '#FFFFFF' : undefined }} />
                    </div>
                    <div>
                      <h3 className="font-semibold">Property Insurance</h3>
                      <p className="text-sm opacity-70">Comprehensive coverage</p>
                    </div>
                  </div>
                  <div className="text-2xl font-bold">AED 2,500/year</div>
                </div>

                <div
                  className="rounded-xl p-6 border ml-8"
                  style={{ backgroundColor: cardBgColor, borderColor: isDCC ? '#4B1F5E' : `${bannerConfig.textColor}20`, color: isDCC ? '#FFFFFF' : undefined }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: chipBgColor }}
                    >
                      <Shield className="h-5 w-5" style={{ color: isDCC ? '#FFFFFF' : undefined }} />
                    </div>
                    <div>
                      <h3 className="font-semibold">Liability Insurance</h3>
                      <p className="text-sm opacity-70">Public & professional</p>
                    </div>
                  </div>
                  <div className="text-2xl font-bold">AED 1,800/year</div>
                </div>

                <div
                  className="rounded-xl p-6 border"
                  style={{ backgroundColor: cardBgColor, borderColor: isDCC ? '#4B1F5E' : `${bannerConfig.textColor}20`, color: isDCC ? '#FFFFFF' : undefined }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: chipBgColor }}
                    >
                      <Shield className="h-5 w-5" style={{ color: isDCC ? '#FFFFFF' : undefined }} />
                    </div>
                    <div>
                      <h3 className="font-semibold">Employee Medical</h3>
                      <p className="text-sm opacity-70">Mandatory coverage</p>
                    </div>
                  </div>
                  <div className="text-2xl font-bold">AED 3,200/year</div>
                </div>
              </div>

              {/* Floating elements */}
              <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center">
                <CheckCircle className="h-8 w-8" style={{ color: isDCC ? '#FFFFFF' : undefined }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
